import React, { useState, useEffect } from "react";
import AuthContext from "./AuthContext";
import api from "../api/api"; // Nuestro servicio API
import Swal from "sweetalert2"; // Para notificaciones

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // Estado del usuario logeado
  const [loading, setLoading] = useState(true); // Estado de carga inicial
  const [token, setToken] = useState(localStorage.getItem("token")); // Obtener token del almacenamiento local

  // Efecto para verificar el token al cargar la aplicación
  useEffect(() => {
    const loadUser = async () => {
      if (token) {
        // Configura la cabecera por defecto para axios si no usas interceptor global
        // api.defaults.headers.common['Authorization'] = `Bearer ${token}`; // Ya cubierto por el interceptor
        try {
          // Llama a un endpoint del backend para obtener los datos del usuario logeado
          // Necesitaremos crear este endpoint en el backend más adelante (/api/auth/user)
          const res = await api.get("/auth/user"); // Ruta protegida
          if (res.data.operation === "SUCCESS") {
            setUser(res.data.user);
          } else {
            // Si el token es inválido o expiró (backend responde ERROR o 401), limpiar token
            logout();
          }
        } catch (err) {
          console.error("Error verificando token:", err);
          // Si el backend responde con 401 Unauthorized, logout
          if (err.response && err.response.status === 401) {
            logout();
          }
          // Si es otro error, quizás manejarlo o simplemente logout
          // logout(); // Depende de cómo quieras manejar errores de red/servidor en la inicialización
        }
      }
      setLoading(false); // Finaliza la carga
    };

    loadUser();
  }, []); // Se ejecuta solo una vez al montar

  // Función para registrar usuario
  const register = async (formData) => {
    try {
      const res = await api.post("/auth/register", formData);
      if (res.data.operation === "SUCCESS") {
        // No logeamos automáticamente tras registro por ahora, solo notificamos
        Swal.fire({
          icon: "success",
          title: "¡Registro Exitoso!",
          text: res.data.message,
        });
        return { success: true }; // Indica éxito para redirigir o cerrar modal
      } else {
        // Manejar errores específicos del backend si los proporciona (ej. email ya existe)
        Swal.fire({
          icon: "error",
          title: "Error en el Registro",
          text: res.data.message || "Ocurrió un error inesperado.",
        });
        return { success: false, message: res.data.message };
      }
    } catch (err) {
      console.error(err.response?.data?.message || err.message);
      // Manejar errores de red o servidor (status 400, 500, etc.)
      Swal.fire({
        icon: "error",
        title: "Error en el Registro",
        text:
          err.response?.data?.message ||
          "Ocurrió un error en la comunicación con el servidor.",
      });
      return {
        success: false,
        message: err.response?.data?.message || "Error desconocido",
      };
    }
  };

  // Función para iniciar sesión
  const login = async (formData) => {
    try {
      const res = await api.post("/auth/login", formData);
      if (res.data.operation === "SUCCESS" && res.data.token) {
        // Si el login es exitoso, almacenar el token y establecer el usuario
        localStorage.setItem("token", res.data.token);
        setToken(res.data.token); // Actualizar el estado del token
        // api.defaults.headers.common['Authorization'] = `Bearer ${res.data.token}`; // Ya cubierto por interceptor

        // Opcional: Puedes llamar a '/auth/user' aquí para obtener los datos completos del usuario
        // o usar los datos básicos que vienen en la respuesta de login (res.data.user)
        setUser(res.data.user); // Establecer el usuario logeado

        Swal.fire({
          icon: "success",
          title: "¡Inicio de Sesión Exitoso!",
          text: "Bienvenido de nuevo.",
          timer: 1500, // Cerrar automáticamente después de 1.5 segundos
          showConfirmButton: false,
        });

        return { success: true }; // Indica éxito para redirigir
      } else {
        // Manejar error de login (credenciales inválidas, etc.)
        Swal.fire({
          icon: "error",
          title: "Error en el Inicio de Sesión",
          text: res.data.message || "Credenciales inválidas.",
        });
        return {
          success: false,
          message: res.data.message || "Credenciales inválidas",
        };
      }
    } catch (err) {
      console.error(err.response?.data?.message || err.message);
      Swal.fire({
        icon: "error",
        title: "Error en el Inicio de Sesión",
        text:
          err.response?.data?.message ||
          "Ocurrió un error en la comunicación con el servidor.",
      });
      return {
        success: false,
        message: err.response?.data?.message || "Error desconocido",
      };
    }
  };

  // Función para cerrar sesión
  const logout = () => {
    localStorage.removeItem("token"); // Eliminar el token
    setToken(null); // Limpiar el estado del token
    setUser(null); // Limpiar el estado del usuario
    // Eliminar la cabecera de autorización por defecto si la hubieras configurado globalmente (el interceptor ya se encarga de no enviarla si token es null)
    // delete api.defaults.headers.common['Authorization'];
    Swal.fire({
      icon: "info",
      title: "Sesión Cerrada",
      text: "Has cerrado sesión correctamente.",
      timer: 1500,
      showConfirmButton: false,
    });
  };

  // Valor que se proporcionará al contexto
  const authValue = {
    user, // El usuario logeado (o null)
    token, // El token JWT (o null)
    isAuthenticated: !!token, // Un booleano si hay token
    loading, // Si el contexto está cargando (verificando token)
    register, // Función para registrar
    login, // Función para login
    logout, // Función para logout
  };

  // El proveedor envuelve a los hijos y les proporciona el valor del contexto
  // No mostramos nada si está cargando para evitar parpadeo antes de verificar el token
  if (loading) return <div>Cargando...</div>; // Puedes usar un spinner o algo mejor

  return (
    <AuthContext.Provider value={authValue}>
      {!loading && children}{" "}
      {/* Renderiza a los hijos solo cuando no está cargando */}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
